import React, { useEffect, useState } from "react"
import { Container, Button, InputGroup, Form } from "react-bootstrap"
import { Bot, Clock3, Pencil, Search, SquarePlus, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom"
import axios from "axios"
import Swal from "sweetalert2"
import { useForm } from "react-hook-form"
import DataTable from "react-data-table-component"
import OrgTableShimmer from "./Common/OrgTableShimmer"
import CompanySelector from "./Common/CompanySelector"
// import OrgTableShimmer from "./Common/OrgTableShimmer"
// import CompanySelector from "./Common/CompanySelector"
const CustomLoader = () => (
  <OrgTableShimmer columns={5} rows={6} titleWidth="180px" />
)
const paginationComponentOptions = {
  selectAllRowsItem: true,
  selectAllRowsItemText: "All",
}

const displayValue = value => {
  const text = value === null || value === undefined ? "" : String(value).trim()
  return text || "--"
}

const ellipsisCell = (value, titlePrefix = "") => {
  const text = displayValue(value)
  return (
    <span className="agent-list-ellipsis" title={titlePrefix ? `${titlePrefix}: ${text}` : text}>
      {text}
    </span>
  )
}

const formatAgentDate = value => {
  if (!value) return "--"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "--"
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  })
}

const getInitials = name => {
  const parts = displayValue(name)
    .replace("--", "")
    .split(/\s+/)
    .filter(Boolean)
  return (parts[0]?.[0] || "A") + (parts[1]?.[0] || "")
}

const agentListTableStyles = {
  table: {
    style: {
      border: "1px solid #d7e3f0",
      borderRadius: "12px",
      overflow: "hidden",
    },
  },
  headRow: {
    style: {
      minHeight: "46px",
      background: "linear-gradient(90deg, #e8f5ff 0%, #eef7f1 100%)",
      borderBottom: "1px solid #c9d9eb",
    },
  },
  headCells: {
    style: {
      color: "#073b63",
      fontSize: "11px",
      fontWeight: 800,
      textTransform: "uppercase",
      paddingLeft: "10px",
      paddingRight: "10px",
      justifyContent: "center",
      textAlign: "center",
    },
  },
  rows: {
    style: {
      minHeight: "54px",
      fontSize: "12px",
      color: "#0f172a",
      borderBottom: "1px solid #e2e8f0",
    },
    stripedStyle: {
      backgroundColor: "#f8fbff",
    },
    highlightOnHoverStyle: {
      backgroundColor: "#edf7ff",
      outline: "1px solid #c8e3fb",
    },
  },
  cells: {
    style: {
      paddingLeft: "10px",
      paddingRight: "10px",
      minWidth: 0,
      justifyContent: "center",
      textAlign: "center",
    },
  },
  pagination: {
    style: {
      minHeight: "54px",
      borderTop: "1px solid #d7e3f0",
      color: "#52657a",
      fontSize: "13px",
    },
  },
}

const Agentlist = () => {
  document.title = "ATS - Agents"
  const navigate = useNavigate()
  const [agents, setAgents] = useState([])
  const [totalRows, setTotalRows] = useState(0)
  const [perPage, setPerPage] = useState(10)
  const [loading, setLoading] = useState(false)
  const [companyId, setCompanyId] = useState(null)
  const handleCompanyChange = c => {
    if (c?.company_id) {
      setCompanyId(c.company_id)
    }
  }
  const { register, getValues } = useForm()
  const fetchAgents = async (page = 1, size = perPage) => {
    setLoading(true)
    const searchValue = getValues("searchValue")
    const isSearching = searchValue && searchValue.trim() !== ""
    try {
      const params = {
        page: page,
        size: size,
      }
      if (isSearching) params.search = searchValue.trim()
      if (companyId) params.company_id = companyId

      const response = await axios.get(`/api/v1/agents/`, { params })
      const res = response.data
      const results = res?.data || res?.results || []
      const count = res?.count || results.length
      setAgents(results)
      setTotalRows(count)
    } catch (error) {
      console.error("Error fetching agents:", error)
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    if (!companyId) return
    fetchAgents()
  }, [companyId])
  const handlePageChange = page => {
    fetchAgents(page, perPage)
  }
  const handlePerRowsChange = async (newPerPage, page) => {
    setPerPage(newPerPage)
    fetchAgents(page, newPerPage)
  }
  const handleDeleteClick = agentId => {
    Swal.fire({
      title: "Are you sure?",
      text: "You want to delete this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(result => {
      if (result.isConfirmed) {
        handleConfirmDelete(agentId)
      }
    })
  }
  const handleConfirmDelete = async agentId => {
    try {
      await axios.delete(`/api/v1/agents/?agent_id=${agentId}`)
      fetchAgents(1, perPage)
      Swal.fire("Success", "Agent deleted successfully!", "success")
    } catch (error) {
      Swal.fire("Error", "Failed to delete agent.", "error")
    }
  }
  const columns = [
    {
      name: "Agent Name",
      selector: row => row.bot_name,
      sortable: true,
      minWidth: "160px",
      grow: 1.2,
      cell: row => {
        const name = displayValue(row.bot_name)
        return (
          <div className="agent-list-name-cell" title={`Agent Name: ${name}`}>
            <span className="agent-list-avatar">{getInitials(name)}</span>
            <span className="agent-list-main-name">{name}</span>
          </div>
        )
      },
    },
    {
      name: "Company Name",
      selector: row => row.agent_company_name,
      sortable: true,
      width: "115px",
      cell: row => ellipsisCell(row.agent_company_name, "Company Name"),
    },
    {
      name: "Company Description",
      selector: row => row.about_the_company,
      sortable: true,
      minWidth: "170px",
      grow: 1.2,
      cell: row => ellipsisCell(row.about_the_company, "Company Description"),
    },
    {
      name: "Company Website",
      selector: row => row.agent_company_website,
      sortable: true,
      cell: row => (
        row.agent_company_website ? <a
          className="agent-list-link"
          href={row.agent_company_website}
          target="_blank"
          rel="noreferrer"
          title={row.agent_company_website}
        >
          {row.agent_company_website}
        </a> : <span className="agent-list-ellipsis">--</span>
      ),
      minWidth: "155px",
    },
    
    {
      name: "Voice",
      selector: row => row.voice_code || "-",
      sortable: true,
      width: "95px",
      cell: row => ellipsisCell(row.voice_code, "Voice"),
    },
    {
      name: "Call Timing",
      selector: row => row.meeting_duration,
      sortable: true,
      width: "110px",
      cell: row => (
        <span className="agent-list-time-pill" title={`Call Timing: ${displayValue(row.meeting_duration)}`}>
          <Clock3 size={13} /> {displayValue(row.meeting_duration)}
        </span>
      ),
    },
    {
      name: "Created At",
      selector: row => row.created_at,
      sortable: true,
      minWidth: "135px",
      cell: row => {
        const val = formatAgentDate(row.created_at)
        return ellipsisCell(val, "Created At")
      },
    },
    {
      name: "Updated At",
      selector: row => row.updated_at,
      sortable: true,
      minWidth: "135px",
      cell: row => {
        const val = formatAgentDate(row.updated_at)
        return ellipsisCell(val, "Updated At")
      },
    },
    {
      name: "Created By",
      sortable: true,
      minWidth: "140px",
      selector: row => [row.created_by_user_first_name, row.created_by_user_middle_name, row.created_by_user_last_name].filter(Boolean).join(" "),
      cell: row => {
        const name = [row.created_by_user_first_name, row.created_by_user_middle_name, row.created_by_user_last_name].filter(Boolean).join(" ") || "-"
        return ellipsisCell(name, "Created By")
      },
    },
    {
      name: "Status",
      grow: 0,
      cell: row => (
        <span
          className={`agent-list-status ${row.is_active ? "agent-list-status-active" : "agent-list-status-inactive"}`}
          title={row.is_active ? "Active" : "Inactive"}
        >
          {row.is_active ? "Active" : "Inactive"}
        </span>
      ),
      width: "88px",
    },
    {
      name: "Action",
      grow: 0,
      center: true,
      cell: row => (
        <div className="d-flex align-items-center justify-content-center gap-2 w-100">
          <Link to={`/agentcreation?id=${row.id}`} className="agent-list-action agent-list-action-edit" title="Edit agent">
            <Pencil size={15} />
          </Link>
          <button
            type="button"
            className="agent-list-action agent-list-action-delete"
            title="Delete agent"
            onClick={() => handleDeleteClick(row.id)}
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
      width: "110px",
    },
  ]
  return (
    <div className="page-content allact-tabs agent-page">
      <Container fluid>
        <div className="agent-list-hero">
          <div className="agent-list-title">
            <div className="agent-list-hero-icon">
              <Bot size={22} />
            </div>
            <div>
              <h1>Agents List</h1>
              <p>Manage TalentScan calling agents, voices, timings, and company profiles.</p>
            </div>
          </div>
          <div className="agent-list-hero-actions">
            <div className="agent-list-hero-controls">
              <div className="agent-list-filter">
                <label>Select Company</label>
                <CompanySelector onCompanyChange={handleCompanyChange} />
              </div>
              <div className="agent-list-search">
                <label>Search Agent</label>
                <InputGroup>
                  <Form.Control
                    placeholder="Search by agent or company"
                    {...register("searchValue")}
                    onKeyDown={e => {
                      if (e.key === "Enter") fetchAgents(1, perPage)
                    }}
                  />
                  <Button variant="outline-primary" onClick={() => fetchAgents(1, perPage)}>
                    <Search size={16} />
                  </Button>
                </InputGroup>
              </div>
              <Button
                variant="primary"
                className="agent-list-create-btn"
                onClick={() => navigate("/agentcreation")}
              >
                <SquarePlus size={17} /> Create Agent
              </Button>
            </div>
            <div className="agent-list-count-card">
              <span>Total Agents</span>
              <strong>{totalRows || 0}</strong>
            </div>
          </div>
        </div>
        <div className="agent-list-table-card" id="AgentList_TableView">
          <DataTable
            columns={columns}
            data={agents}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={perPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            highlightOnHover
            striped
            responsive
            paginationComponentOptions={paginationComponentOptions}
            persistTableHead
            fixedHeader
            fixedHeaderScrollHeight="700px"
            customStyles={agentListTableStyles}
            progressPending={loading}
            progressComponent={<CustomLoader />}
            noDataComponent={
              <div className="agent-list-empty">
                <Bot size={26} />
                <span>No agents found.</span>
              </div>
            }
          />
        </div>
      </Container>
    </div>
  )
}
export default Agentlist
